import React, { useEffect, useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CoverImageUpload = ({ coverImage, onImageChange, error, headerActions = null }) => {
  const [previewUrl, setPreviewUrl] = useState(coverImage || null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setPreviewUrl(coverImage || null);
  }, [coverImage]);

  const handleFileSelect = (file) => {
    if (file && file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader?.result);
        onImageChange(reader?.result, file);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleFileInput = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onImageChange(null, null);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
          커버 이미지
        </label>
        <p className="text-xs md:text-sm text-muted-foreground caption">
          챌린지를 대표하는 이미지를 업로드하세요 (권장: 1200x630px)
        </p>
        {headerActions ? (
          <div className="mt-3 flex items-center justify-end">
            {headerActions}
          </div>
        ) : null}
      </div>

      {previewUrl ? (
        <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted">
          <Image
            src={previewUrl}
            alt="Challenge cover image preview showing uploaded visual representation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background/20 flex items-end p-4 md:p-6">
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={handleRemove}
            >
              이미지 제거
            </Button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative w-full aspect-video rounded-lg border-2 border-dashed transition-gaming ${
            isDragging
              ? 'border-primary bg-primary/10' :'border-border bg-muted/50 hover:border-muted hover:bg-muted'
          }`}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id="cover-image-input"
          />
          <label
            htmlFor="cover-image-input"
            className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer p-4"
          >
            <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/20 rounded-md flex items-center justify-center mb-3 md:mb-4">
              <Icon name="Upload" size={24} color="var(--color-primary)" />
            </div>
            <p className="text-sm md:text-base font-medium text-foreground mb-1 text-center">
              이미지를 드래그하거나 클릭하여 업로드
            </p>
            <p className="text-xs text-muted-foreground caption text-center">
              PNG, JPG, GIF (최대 5MB)
            </p>
          </label>
        </div>
      )}

      {error && (
        <div className="flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-destructive">{error}</p>
        </div>
      )}
    </div>
  );
};

export default CoverImageUpload;